import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Input,
  message,
  Modal,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import {
  Breadcrumbs,
  type BreadcrumbItem,
} from '../../components/layout/Breadcrumbs';
import { PromotionTypeTag } from '../../components/shop-promotions/PromotionTypeTag';
import {
  deleteShopPromotion,
  fetchShopPromotions,
} from '../../services/shopPromotionsService';
import type {
  ShopPromotionRecord,
  ShopPromotionType,
} from '../../types/shopPromotion';
import {
  formatDateTimeRange,
  formatPromotionValueSummary,
} from '../../utils/shopPromotionLabels';
import api from '../../services/api';
import type { MerchantOption } from '../../types/shopPromotion';

const PAGE_SIZE = 20;

export default function ShopPromotionsListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ShopPromotionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState<ShopPromotionType | ''>('');
  const [restaurantFilter, setRestaurantFilter] = useState<
    number | undefined
  >();
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const merchantIdByRestaurant = React.useMemo(() => {
    const map = new Map<number, number>();
    merchants.forEach(m => map.set(m.restaurantId, m.merchantId));
    return map;
  }, [merchants]);

  const loadMerchants = useCallback(async () => {
    try {
      const res = await api.get<{
        success: boolean;
        data: Array<{
          id: number;
          name: string;
          restaurantId?: number;
          restaurantName?: string;
        }>;
      }>('/merchants', { params: { page: 1, limit: 500 } });
      if (!res.data.success) return;
      setMerchants(
        (res.data.data ?? [])
          .map(m => {
            const restaurantId = m.restaurantId ?? m.id;
            return {
              merchantId: m.id,
              restaurantId,
              label: m.restaurantName || m.name,
            };
          })
          .filter(m => m.restaurantId)
      );
    } catch {
      /* ignore */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchShopPromotions({
        page,
        limit: PAGE_SIZE,
        search: search.trim() || undefined,
        discount_type: typeFilter || undefined,
        restaurant_id: restaurantFilter,
        shop_only: true,
      });
      if (res.success && Array.isArray(res.data)) {
        setRows(res.data);
        setTotal(res.pagination?.total ?? res.data.length);
      } else {
        setRows([]);
        setTotal(0);
        setError(res.message || 'Không tải được danh sách');
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message || 'Lỗi tải dữ liệu');
      message.error('Không tải được khuyến mãi quán');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, restaurantFilter]);

  useEffect(() => {
    loadMerchants();
  }, [loadMerchants]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = (record: ShopPromotionRecord) => {
    Modal.confirm({
      title: 'Xóa khuyến mãi?',
      content: (
        <>
          Mã <strong>{record.code}</strong> — quán{' '}
          <strong>{record.restaurant_name ?? record.restaurant_id}</strong>.
          {!record.is_active ? null : (
            <p style={{ marginTop: 8, color: '#cf1322' }}>
              Cần tắt khuyến mãi trước khi xóa.
            </p>
          )}
        </>
      ),
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          const res = await deleteShopPromotion(record.id);
          if (res.success) {
            message.success('Đã xóa khuyến mãi');
            load();
          } else {
            message.error(res.message || 'Xóa thất bại');
          }
        } catch (e: unknown) {
          const ax = e as { response?: { data?: { message?: string } } };
          message.error(ax.response?.data?.message || 'Xóa thất bại');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Mã / Tên',
      key: 'code',
      render: (_: unknown, r: ShopPromotionRecord) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.code}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{r.name || '–'}</div>
        </div>
      ),
    },
    {
      title: 'Quán',
      key: 'restaurant',
      width: 200,
      render: (_: unknown, r: ShopPromotionRecord) =>
        r.restaurant_id != null ? (
          <Link
            to={`/merchants/${
              merchantIdByRestaurant.get(r.restaurant_id) ?? r.restaurant_id
            }`}
          >
            <ShopOutlined /> {r.restaurant_name ?? `Quán #${r.restaurant_id}`}
          </Link>
        ) : (
          '–'
        ),
    },
    {
      title: 'Loại',
      key: 'type',
      width: 130,
      render: (_: unknown, r: ShopPromotionRecord) => (
        <PromotionTypeTag type={r.discount_type} />
      ),
    },
    {
      title: 'Giá trị',
      key: 'value',
      width: 160,
      render: (_: unknown, r: ShopPromotionRecord) =>
        formatPromotionValueSummary(r),
    },
    {
      title: 'Thời gian',
      key: 'period',
      width: 220,
      render: (_: unknown, r: ShopPromotionRecord) =>
        formatDateTimeRange(r.valid_from, r.valid_until),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 100,
      render: (_: unknown, r: ShopPromotionRecord) => (
        <Tag color={r.is_active ? 'success' : 'default'}>
          {r.is_active ? 'Bật' : 'Tắt'}
        </Tag>
      ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 140,
      render: (_: unknown, r: ShopPromotionRecord) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/shop-promotions/${r.id}`)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/shop-promotions/${r.id}/edit`)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(r)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumbs
        items={
          [
            { label: 'Trang chủ', path: '/' },
            { label: 'KM quán (Combo & BXGY)' },
          ] satisfies BreadcrumbItem[]
        }
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <h2 style={{ margin: 0 }}>Khuyến mãi quán (KM, Combo, BXGY)</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/shop-promotions/create')}
        >
          Thêm khuyến mãi
        </Button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16 }}
          closable
        />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Tìm mã hoặc tên"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onPressEnter={() => {
              setSearch(searchInput.trim());
              setPage(1);
            }}
            allowClear
            style={{ width: 220 }}
          />
          <Select
            placeholder="Loại KM"
            allowClear
            style={{ width: 160 }}
            value={typeFilter || undefined}
            onChange={v => {
              setTypeFilter((v as ShopPromotionType) || '');
              setPage(1);
            }}
            options={[
              { value: 'percentage', label: 'Giảm %' },
              { value: 'fixed_amount', label: 'Giảm cố định' },
              { value: 'bxgy', label: 'BXGY' },
              { value: 'combo', label: 'Combo' },
            ]}
          />
          <Select
            placeholder="Lọc theo quán"
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 220 }}
            value={restaurantFilter}
            onChange={v => {
              setRestaurantFilter(v);
              setPage(1);
            }}
            options={merchants.map(m => ({
              value: m.restaurantId,
              label: m.label,
            }))}
          />
          <Button
            onClick={() => {
              setSearch(searchInput.trim());
              setPage(1);
            }}
          >
            Tìm
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            onChange: p => setPage(p),
          }}
        />
      </Card>
    </div>
  );
}
